import { useMemo, useRef } from 'react'
import { formatNumberWithSuffix } from 'src/utils/formatNumberWithSuffix'
import { useClientSideTranslation } from 'src/services/i18n'
import { useGetCurrentLanguage } from 'src/store/game'
import TrashIcon from 'src/app/assets/icons/ico-trash.svg'
import RepeatIcon from 'src/app/assets/icons/ico-redo-alt.svg'
// The ladder's chip families live in the PURE module (chipStack.ts) — one
// family per rung, rim-view separability pinned by tests (v13 fix round).
import { chipStyleForIndex } from './chipStack'

type Props = Readonly<{
  chips: number[]
  selectedChipIdx: number
  onSelect: (i: number) => void
  onClear: () => void
  onDouble: () => void
  onRepeat: () => void
  // Whether the board has stakes to double / clear (drives showing ×2 vs
  // Repeat, and enabling Clear).
  canControl: boolean
  // Whether a previous round's stakes exist to repeat (drives Repeat).
  canRepeat: boolean
  // Betting window closed → the whole strip is inert.
  disabled: boolean
  // Fires the ×2 punch feedback the same frame as `onDouble`.
  doublePunchAt: number
}>

/**
 * Denomination chip selector + Clear / ×2 / Repeat controls shared across
 * both bet zones. Sits below the zones (per the crash layout). No Undo —
 * retired everywhere (mobile v36, desktop v45): trash + ×2/Repeat cover the
 * correction flows, and the leaner strip reads calmer. Mirrors
 * `ChipPlacementPanel`'s `chip-panel__bar`: chip discs with a selected-chip
 * scale + spinning dashed ring, a mobile 3-chip carousel driven by
 * `--chip-slot`, and instrument-glass control buttons.
 */
export function CrashChipStrip({
  chips,
  selectedChipIdx,
  onSelect,
  onClear,
  onDouble,
  onRepeat,
  canControl,
  canRepeat,
  disabled,
  doublePunchAt
}: Props) {
  const { t } = useClientSideTranslation(useGetCurrentLanguage())
  // Start-X of a mobile swipe on the chip strip, to detect direction.
  const swipeStartX = useRef<number | null>(null)

  const safeChipIdx = Math.min(Math.max(0, selectedChipIdx), Math.max(0, chips.length - 1))

  // The 3-chip window (mobile) is centred on the selected chip; the CSS
  // positions each chip via `--chip-slot = idx - windowStart`.
  const windowStart = useMemo(() => {
    const last = chips.length - 1
    return Math.max(0, Math.min(Math.max(0, last - 2), safeChipIdx - 1))
  }, [chips.length, safeChipIdx])

  // ×2 shows only when there's something to double; otherwise Repeat (when a
  // previous round exists). Undo/Clear enable with placed stakes.
  const showDouble = canControl
  const showRepeat = !canControl && canRepeat

  return (
    <div className={`crash-chip-strip ${disabled ? 'crash-chip-strip--disabled' : ''}`}>
      {/* LEFT: Clear */}
      <div className="crash-chip-strip__actions crash-chip-strip__actions--left">
        <button
          type="button"
          className="crash-chip-strip__control crash-chip-strip__control--clear"
          onClick={onClear}
          disabled={disabled || !canControl}
          title={t('Clear')}
          aria-label={t('Clear')}
        >
          <TrashIcon />
        </button>
      </div>

      {/* CHIPS */}
      <div
        className="crash-chip-strip__chips"
        onTouchStart={(e) => {
          swipeStartX.current = e.touches[0]?.clientX ?? null
        }}
        onTouchEnd={(e) => {
          const startX = swipeStartX.current
          swipeStartX.current = null
          if (startX === null || disabled) return
          const endX = e.changedTouches[0]?.clientX
          if (endX === undefined) return
          const dx = endX - startX
          if (Math.abs(dx) < 30) return
          const nextIdx = dx < 0 ? safeChipIdx + 1 : safeChipIdx - 1
          const clamped = Math.max(0, Math.min(chips.length - 1, nextIdx))
          if (clamped !== safeChipIdx) onSelect(clamped)
        }}
      >
        <div className="crash-chip-strip__chip-track">
          {chips.map((value, idx) => {
            const slot = idx - windowStart
            const inWindow = slot >= 0 && slot <= 2
            const style = chipStyleForIndex(idx)
            return (
              <button
                key={`${value}-${idx}`}
                type="button"
                className={`crash-chip-strip__chip ${safeChipIdx === idx ? 'crash-chip-strip__chip--active' : ''} ${inWindow ? 'crash-chip-strip__chip--in-window' : ''}`}
                style={{
                  background: style.bg,
                  borderColor: style.border,
                  color: style.color,
                  ['--chip-slot' as string]: slot
                }}
                disabled={disabled}
                onClick={() => onSelect(idx)}
              >
                {formatNumberWithSuffix(value, t)}
              </button>
            )
          })}
        </div>
      </div>

      {/* RIGHT: ×2 / Repeat */}
      <div className="crash-chip-strip__actions crash-chip-strip__actions--right">
        {showRepeat ? (
          <button
            type="button"
            className="crash-chip-strip__control crash-chip-strip__control--repeat"
            onClick={onRepeat}
            disabled={disabled}
            title={t('Repeat')}
            aria-label={t('Repeat')}
          >
            <RepeatIcon />
          </button>
        ) : (
          <button
            type="button"
            className={`crash-chip-strip__control crash-chip-strip__control--double ${doublePunchAt > 0 ? 'crash-chip-strip__control--punch' : ''}`}
            onClick={onDouble}
            disabled={disabled || !showDouble}
            title={t('Double')}
            aria-label={t('Double')}
          >
            <span>×2</span>
          </button>
        )}
      </div>
    </div>
  )
}
